import { Modal, Form, Select, Input, message } from 'antd';
import AxiosOICStat from "AxiosOICStat";
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

const { Option } = Select;

const IrenaIndicatorMappingModal = ({ visible, onCancel, onSuccess, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [irenaItems, setIrenaItems] = useState([]);
  const [irenaSearch, setIrenaSearch] = useState('');
  const searchTimer = useRef(null);
  const indicatorsState = useSelector((state) => state.indicators);

  const availableIndicators = indicatorsState.value
    ? Object.values(indicatorsState.value).sort((a, b) => (a.ind_name_eng || '').localeCompare(b.ind_name_eng || ''))
    : [];

  useEffect(() => {
    if (visible) {
      fetchIrenaItems('');
      setIrenaSearch('');
    }
  }, [visible]);

  const fetchIrenaItems = async (search = '') => {
    try {
      const q = [];
      if (typeof search === 'string' && search.trim()) q.push(`search=${encodeURIComponent(search.trim())}`);
      q.push('limit=200');
      const url = `/external_api/irena/get-all-irena-data?${q.join('&')}`;
      const res = await AxiosOICStat.get(url);
      const items = res.data?.items || res.data || [];
      setIrenaItems(Array.isArray(items) ? items : []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchIrenaItems(irenaSearch || '');
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [irenaSearch]);

  useEffect(() => {
    if (visible && record) {
      form.setFieldsValue({
        oic_ind_code: record.oic_ind_code ? String(record.oic_ind_code) : undefined,
        oic_ind_name: record.oic_ind_name,
        irena_indicator_code: record.irena_indicator_code,
        irena_series_name: record.irena_series_name,
        irena_table: record.irena_table,
        technology_type: record.technology_type,
        technology_codes: record.technology_codes,
        grid_type: record.grid_type,
        unit: record.unit,
        _irena_item_id: undefined,
      });
    }
    if (!visible && !record) form.resetFields();
  }, [visible, record, form]);

  const handleIrenaSelect = (selectedCode) => {
    const item = irenaItems.find(i => i.irena_indicator_code === selectedCode);
    if (item) {
      form.setFieldsValue({
        irena_indicator_code: item.irena_indicator_code,
        irena_series_name: item.technology_label || item.irena_indicator_code,
        irena_table: item.irena_table,
        technology_type: item.technology_type,
        technology_codes: item.technology_code,
        grid_type: item.grid_type,
        unit: item.unit,
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (record && record.id) {
        await AxiosOICStat.put(`/external_api/irena/indicator-mappings/${record.id}`, values);
        message.success('Indicator mapping updated');
      } else {
        await AxiosOICStat.post('/external_api/irena/indicator-mappings', values);
        message.success('Indicator mapping created');
      }
      onSuccess();
    } catch (err) {
      if (err.errorFields) return;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to save mapping';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={record ? 'Edit IRENA Indicator Mapping' : 'Add IRENA Indicator Mapping'}
      visible={visible}
      onOk={handleSubmit}
      onCancel={() => { form.resetFields(); onCancel(); }}
      confirmLoading={loading}
      width={500}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="oic_ind_code"
          label="OIC Indicator"
          rules={[{ required: true, message: 'Please select an OIC indicator' }]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            placeholder="Select an indicator"
            onChange={(val) => {
              const sel = availableIndicators.find(i => String(i.ind_code) === String(val));
              if (sel) {
                form.setFieldsValue({ oic_ind_name: sel.ind_name_eng });
              }
            }}
            optionFilterProp="children"
          >
            {availableIndicators.map(ind => (
              <Option key={String(ind.ind_code)} value={String(ind.ind_code)}>{ind.ind_code} - {ind.ind_name_eng}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="oic_ind_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item
          name="_irena_item_id"
          label="IRENA Indicator"
          rules={[{ required: true, message: 'Please select an IRENA indicator' }]}
        >
          <Select
            showSearch
            allowClear
            placeholder="Search and select IRENA indicator..."
            searchValue={irenaSearch}
            onSearch={(val) => setIrenaSearch(val)}
            autoClearSearchValue={false}
            onChange={handleIrenaSelect}
            filterOption={false}
          >
            {irenaItems.map(item => {
              const code = item.irena_indicator_code || '';
              const table = item.irena_table || '';
              const tech = item.technology_type || '';
              const grid = item.grid_type || '';
              const label = `${code} | ${table} | ${tech}${grid ? ` | ${grid}` : ''}`;
              return (
                <Option key={code} value={code}>{label}</Option>
              );
            })}
          </Select>
        </Form.Item>

        <Form.Item name="irena_indicator_code" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name="irena_series_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name="irena_table" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name="technology_type" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name="technology_codes" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name="grid_type" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name="unit" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default IrenaIndicatorMappingModal;
